import { useMemo, useState, useEffect } from "react";
import { Search, Loader2, User, Users, ChevronDown, ChevronUp, Zap, X, BarChart3, Power, Flag, ArrowRight, AlertTriangle, Building, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatsCards from "@/components/StatsCards";
import FilterBar from "@/components/FilterBar";
import PropertyCard from "@/components/PropertyCard";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VIEW_MODES = [
  { value: "titolare", label: "Tutti", icon: Users },
  { value: "Stefano", label: "Stefano", icon: User, category: "commerciale" },
  { value: "Giorgia", label: "Giorgia", icon: User, category: "commerciale" },
  { value: "Domenico", label: "Domenico", icon: User, category: "residenziale" },
  { value: "Filippo", label: "Filippo", icon: User, category: "residenziale" },
  { value: "Ivan", label: "Ivan", icon: User, category: "residenziale" },
];

const AGENTS_LIST = ["Domenico", "Filippo", "Ivan", "Stefano", "Giorgia"];

function SegnalazioneCard({ segnalazione, onResolve }) {
  const [reassignTo, setReassignTo] = useState("");
  const [processing, setProcessing] = useState(false);
  const s = segnalazione;

  const handleReassign = async () => {
    if (!reassignTo) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/properties/${s.property_id}/reassign`, { agent: reassignTo });
      await axios.put(`${API}/segnalazioni/${s.id}/resolve`, { action_taken: `Riassegnato a ${reassignTo}` });
      onResolve(s.id);
    } catch { /* silent */ }
    setProcessing(false);
  };

  return (
    <div data-testid={`segnalazione-${s.id}`} className="bg-white border border-orange-200 rounded-sm p-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-zinc-400">{s.ref_code}</span>
          <span className="text-sm font-semibold text-zinc-800 truncate">{s.property_title}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border ${s.property_category === "commerciale" ? "text-indigo-700 bg-indigo-50 border-indigo-200" : "text-teal-700 bg-teal-50 border-teal-200"}`}>
            {s.property_category || "residenziale"}
          </span>
          {s.source_urls && Object.entries(s.source_urls).map(([portal, url]) => (
            url && <a key={portal} href={url} target="_blank" rel="noopener noreferrer" className="text-[9px] font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2">{portal}</a>
          ))}
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          <span className="font-semibold text-orange-600">{s.reported_by}</span>
          {" → "}{s.motivo}
        </div>
        <div className="text-[10px] text-zinc-400 mt-0.5">
          Precedentemente assegnato a: <span className="font-bold text-zinc-600">{s.current_agent || "nessuno"}</span>
          {" · "}<span className="text-orange-500 font-semibold">IN STANDBY</span>
          {" · "}{new Date(s.created_at).toLocaleString("it-IT")}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Select value={reassignTo} onValueChange={setReassignTo}>
          <SelectTrigger data-testid={`reassign-select-${s.id}`} className="h-8 w-36 rounded-sm border-zinc-300 text-xs">
            <SelectValue placeholder="Assegna a..." />
          </SelectTrigger>
          <SelectContent>
            {AGENTS_LIST.map(a => (
              <SelectItem key={a} value={a}>
                {a}{a === s.current_agent ? " (era qui)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          data-testid={`reassign-${s.id}`}
          onClick={handleReassign}
          disabled={!reassignTo || processing}
          size="sm"
          className="h-8 px-3 bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm text-xs font-semibold"
        >
          {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ArrowRight className="w-3 h-3 mr-1" />Assegna</>}
        </Button>
      </div>
    </div>
  );
}

export default function Dashboard({
  stats, properties, portals, comuni, loading, searchLoading, searchProgress, filters,
  viewMode, onViewModeChange,
  onRunSearch, onUpdateStatus, onApplyFilters,
  searchReport, onCloseReport, user, todayAssignments: todayAssignmentsProp
}) {
  const isTitolare = user?.role === "titolare";
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [cronEnabled, setCronEnabled] = useState(true);
  const [cronLoading, setCronLoading] = useState(false);
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [segnalazioniLoading, setSegnalazioniLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [multiAgency, setMultiAgency] = useState([]);
  // todayAssignments viene passato come prop da App.js (aggiornato dopo ogni ricerca)
  const todayAssignments = todayAssignmentsProp || null;
  // Vista Team: agente vede tutti gli immobili del team (sola lettura)
  // Default: Vista Team attiva (così gli agenti vedono subito le assegnazioni dei colleghi)
  const [teamView, setTeamView] = useState(true);
  // Vista Team: ordinamento per agente (gruppi)
  const [sortByAgent, setSortByAgent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/scraping/credits`).then(r => setCredits(r.data)).catch(() => {});
    if (isTitolare) {
      axios.get(`${API}/settings/cron`).then(r => setCronEnabled(r.data.enabled)).catch(() => {});
      axios.get(`${API}/segnalazioni?status=pending`).then(r => setSegnalazioni(r.data || [])).catch(() => {});
      axios.get(`${API}/agency-multi`).then(r => setMultiAgency(r.data || [])).catch(() => {});
    }
  }, [isTitolare]);

  // Filter by viewMode (agent view shows only their assignments)
  const filteredProperties = useMemo(() => {
    let result = properties;

    // Hide residential affitti unless toggle is on
    // Commercial affitti are ALWAYS shown
    if (!filters.show_affitto) {
      result = result.filter(p =>
        p.property_category === "commerciale" || p.listing_type !== "affitto"
      );
    }

    // Agent view filter
    if (viewMode && viewMode !== "titolare") {
      if (teamView) {
        // Vista Team: mostra tutti gli immobili della categoria dell'agente (sola lettura)
        if (viewMode === "Giorgia" || viewMode === "Stefano") {
          result = result.filter(p => p.property_category === "commerciale");
        } else {
          result = result.filter(p => !p.property_category || p.property_category !== "commerciale");
        }
      } else if (viewMode === "Giorgia" || viewMode === "Stefano") {
        // Giorgia e Stefano (commerciale): vedono tutti gli immobili commerciali
        result = result.filter(p => p.property_category === "commerciale");
      } else {
        // Other agents see only their residential properties (vendita only)
        result = result.filter(p =>
          p.assigned_agent === viewMode &&
          (!p.property_category || p.property_category !== "commerciale")
        );
      }
    }

    // Titolare category toggle (Tutti view)
    if (viewMode === "titolare" && categoryFilter !== "all") {
      if (categoryFilter === "commerciale") {
        result = result.filter(p => p.property_category === "commerciale");
      } else {
        result = result.filter(p => !p.property_category || p.property_category !== "commerciale");
      }
    }

    // Additional agent filter from FilterBar
    if (filters.agent) {
      result = result.filter(p => p.assigned_agent === filters.agent);
    }

    // Hide discarded unless explicitly filtering for them
    if (!filters.status || filters.status !== "discarded") {
      result = result.filter(p => p.status !== "discarded");
    }

    // Status filter from StatsCards click
    if (statusFilter) {
      result = result.filter(p => p.status === statusFilter);
    }

    return result;
  }, [properties, viewMode, teamView, categoryFilter, filters.agent, filters.show_affitto, filters.status, statusFilter]);

  // Sort: discarded at bottom
  const sortedProperties = useMemo(() => {
    return [...filteredProperties].sort((a, b) => {
      if (a.status === "discarded" && b.status !== "discarded") return 1;
      if (a.status !== "discarded" && b.status === "discarded") return -1;
      // Vista Team con ordinamento per agente
      if (teamView && sortByAgent) {
        const agA = a.assigned_agent || "\uFFFF"; // non assegnati in fondo
        const agB = b.assigned_agent || "\uFFFF";
        if (agA !== agB) return agA.localeCompare(agB, "it");
      }
      return 0;
    });
  }, [filteredProperties, teamView, sortByAgent]);

  const isAgentView = viewMode && viewMode !== "titolare";
  const currentAgentFirstName = user?.name?.split(" ")[0] || "";
  // In Vista Team: solo gli immobili degli ALTRI agenti sono sola lettura.
  // I propri restano modificabili anche in Vista Team.
  // Il titolare non è mai in readOnly.
  // In Vista Team: solo le card degli ALTRI agenti sono sola lettura — le proprie restano modificabili.

  // Compute stats based on viewMode
  const filteredStats = useMemo(() => {
    if (!stats) return stats;
    if (viewMode === "titolare") return stats;
    return {
      ...stats,
      total: filteredProperties.length,
      new: filteredProperties.filter(p => p.status === "new").length,
      interesting: filteredProperties.filter(p => p.status === "interesting").length,
      contacted: filteredProperties.filter(p => p.status === "contacted").length,
      acquired: filteredProperties.filter(p => p.status === "acquired").length,
    };
  }, [stats, viewMode, filteredProperties]);

  // Load last search report on mount (for Titolare)
  useEffect(() => {
    if (isTitolare && !searchReport) {
      axios.get(`${API}/search/report`).then(res => {
        if (res.data?.comuni_report) {
          // Don't auto-show, just make it available
        }
      }).catch(() => {});
    }
  }, [isTitolare, searchReport]);

  const [showLastReport, setShowLastReport] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  
  const loadLastReport = async () => {
    try {
      const res = await axios.get(`${API}/search/report`);
      if (res.data) {
        setLastReport(res.data);
        setShowLastReport(true);
      }
    } catch (e) { console.debug("Report load failed", e); }
  };

  const activeReport = searchReport || (showLastReport ? lastReport : null);

  return (
    <div data-testid="dashboard-page" className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {isTitolare && credits && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-zinc-500 font-medium">
                <Zap className="w-3.5 h-3.5 inline mr-1" />
                {credits.completed_searches} ricerche
              </span>
              {credits.proxies && Object.entries(credits.proxies).map(([name, info]) => {
                const isError = !!info.error;
                const isFree = !!info.free;
                const pct = info.remaining_percent ?? (info.usage_percent != null ? 100 - info.usage_percent : null);
                const resetsAt = info.resets_at;
                const color = isError ? "text-red-600" : isFree ? "text-emerald-600" : pct != null && pct < 20 ? "text-red-500" : pct != null && pct < 50 ? "text-amber-500" : "text-emerald-600";
                return (
                  <span key={name} className={`text-xs font-semibold ${color}`}>
                    {name}:&nbsp;
                    {isError
                      ? <span className="text-red-600 font-bold">{info.error}</span>
                      : isFree
                        ? <span className="text-emerald-600">gratuito ∞</span>
                        : <span>{pct != null ? `${Math.round(pct)}% crediti` : "attivo"}{resetsAt ? <span className="text-zinc-400 font-normal ml-1">(↺ {resetsAt})</span> : null}</span>
                    }
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {isTitolare && (
            <button
              data-testid="cron-toggle-btn"
              onClick={async () => {
                setCronLoading(true);
                try {
                  const res = await axios.post(`${API}/settings/cron`);
                  setCronEnabled(res.data.enabled);
                } catch {}
                setCronLoading(false);
              }}
              disabled={cronLoading}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-semibold border transition-all ${
                cronEnabled
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  : "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
              }`}
              title={cronEnabled ? "Ricerca automatica ATTIVA — clicca per disattivare" : "Ricerca automatica DISATTIVATA — clicca per attivare"}
            >
              <Power className="w-3.5 h-3.5" />
              {cronEnabled ? "Auto ON" : "Auto OFF"}
            </button>
          )}
          {stats?.last_search && (
            <div data-testid="last-update-info" className="flex flex-col items-start sm:items-end mr-2">
              <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-400">Ultimo aggiornamento</span>
              <span className="text-xs font-bold text-zinc-600">
                {new Date(stats.last_search).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                {" "}
                {new Date(stats.last_search).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          )}
          {isTitolare && !activeReport && (
            <Button
              data-testid="last-report-btn"
              variant="outline"
              onClick={loadLastReport}
              className="rounded-sm h-9 sm:h-10 px-3 text-xs font-semibold"
            >
              <BarChart3 className="w-4 h-4 mr-1" />
              Ultimo Report
            </Button>
          )}
          {isTitolare && (
            <Button
              data-testid="manual-search-btn"
              onClick={onRunSearch}
              disabled={searchLoading}
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-[0.98] w-full sm:w-auto"
            >
              {searchLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ricerca...</>
              ) : (
                <><Search className="w-4 h-4 mr-2" />Lancia Ricerca</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Search Progress Bar */}
      {searchLoading && (
        <div data-testid="search-progress" className="border-2 border-zinc-900 bg-zinc-900 rounded-sm p-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-bold">{searchProgress?.step || "Avvio ricerca..."}</span>
            </div>
            <span className="text-sm font-black tabular-nums">{searchProgress?.percent || 0}%</span>
          </div>
          <div className="w-full bg-zinc-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${searchProgress?.percent || 0}%` }}
            />
          </div>
          {searchProgress?.detail && (
            <p className="text-xs text-zinc-400 mt-1.5">{searchProgress.detail}</p>
          )}
        </div>
      )}

      {/* Search Report Panel — solo Titolare */}
      {activeReport && isTitolare && (
        <div data-testid="search-report" className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-4 relative">
          <button
            onClick={() => { onCloseReport(); setShowLastReport(false); }}
            className="absolute top-2 right-2 p-1 hover:bg-emerald-100 rounded-sm text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-bold text-zinc-900">Riepilogo Ricerca</h3>
            <span className="text-[10px] text-zinc-400">
              {activeReport.completed_at ? new Date(activeReport.completed_at).toLocaleString("it-IT") : ""}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 bg-white rounded-sm border border-emerald-100">
              <div className="text-lg font-black text-emerald-700">{activeReport.new_residential || 0}</div>
              <div className="text-[10px] uppercase font-semibold text-zinc-400">Nuovi residenziali</div>
            </div>
            <div className="text-center p-2 bg-white rounded-sm border border-emerald-100">
              <div className="text-lg font-black text-indigo-700">{activeReport.new_commercial || 0}</div>
              <div className="text-[10px] uppercase font-semibold text-zinc-400">Nuovi commerciali</div>
            </div>
            <div className="text-center p-2 bg-white rounded-sm border border-emerald-100">
              <div className="text-lg font-black text-zinc-500">{activeReport.merged_count || 0}</div>
              <div className="text-[10px] uppercase font-semibold text-zinc-400">Duplicati</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="grid grid-cols-[1fr,60px,60px] gap-2 text-[10px] uppercase font-bold text-zinc-400 px-1">
              <span>Portale</span>
              <span className="text-right">Privati</span>
              <span className="text-right">Agenzie</span>
            </div>
            {Object.entries(activeReport.portal_results || {})
              .filter(([, v]) => typeof v === 'object' && (v.privati > 0 || v.agenzie > 0))
              .sort(([, a], [, b]) => (b.privati || 0) - (a.privati || 0))
              .map(([portal, data]) => (
                <div key={portal} className="grid grid-cols-[1fr,60px,60px] gap-2 text-xs px-1 py-0.5 hover:bg-white/50 rounded-sm">
                  <span className="font-medium text-zinc-700 truncate">{portal}</span>
                  <span className="text-right font-bold text-emerald-700">{data.privati || 0}</span>
                  <span className="text-right text-zinc-400">{data.agenzie || 0}</span>
                </div>
              ))
            }
          </div>
          {activeReport.errors?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-red-200">
              <span className="text-[10px] font-bold text-red-500">Errori: </span>
              <span className="text-[10px] text-red-400">{activeReport.errors.join(", ")}</span>
            </div>
          )}
          {/* Report Comuni */}
          {activeReport.comuni_report && Object.keys(activeReport.comuni_report).length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200">
              <h4 className="text-xs font-bold text-zinc-700 mb-2">Report Comuni</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
                {Object.entries(activeReport.comuni_report)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([comune, data]) => (
                    <div key={comune} className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] ${
                      data.status === "error" ? "bg-red-50 border border-red-200" :
                      data.privati > 0 ? "bg-emerald-50 border border-emerald-200" :
                      "bg-zinc-50 border border-zinc-200"
                    }`}>
                      <span className="shrink-0">
                        {data.status === "error" ? "❌" : data.privati > 0 ? "✅" : "➖"}
                      </span>
                      <span className="font-medium text-zinc-700 truncate">{comune}</span>
                      {data.privati > 0 && (
                        <span className="ml-auto text-emerald-700 font-bold shrink-0">{data.privati}</span>
                      )}
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* View mode selector — solo Titolare */}
      {isTitolare && (
        <div data-testid="view-mode-selector" className="flex items-center gap-1 p-1 bg-zinc-100 rounded-sm overflow-x-auto max-w-full">
          {VIEW_MODES.map(mode => {
            const Icon = mode.icon;
            const active = viewMode === mode.value;
            return (
              <button
                key={mode.value}
                data-testid={`view-mode-${mode.value}`}
                onClick={() => onViewModeChange(mode.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
                  active
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {mode.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Toggle I Miei / Vista Team — solo Agenti */}
      {!isTitolare && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div data-testid="team-view-toggle" className="flex items-center gap-1 p-1 bg-zinc-100 rounded-sm w-fit">
            <button
              data-testid="toggle-my-view"
              onClick={() => setTeamView(false)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
                !teamView ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
              }`}
            >
              <User className="w-3.5 h-3.5" />
              I Miei
            </button>
            <button
              data-testid="toggle-team-view"
              onClick={() => setTeamView(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
                teamView ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Vista Team
            </button>
          </div>
          {teamView && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-zinc-500 italic">
                Vista Team — i tuoi annunci sono modificabili, quelli dei colleghi in sola lettura
              </span>
              <button
                data-testid="sort-by-agent-btn"
                onClick={() => setSortByAgent(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border transition-all ${
                  sortByAgent
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"
                }`}
              >
                <ArrowUpDown className="w-3 h-3" />
                {sortByAgent ? "Ordine: per Agente" : "Ordine: per Data"}
              </button>
            </div>
          )}        </div>
      )}

      {/* Agent banner — for Titolare impersonating */}
      {isTitolare && isAgentView && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-2 flex items-center justify-between">
          <span className="text-sm text-zinc-600">
            Stai visualizzando come: <strong className="text-zinc-900">{viewMode}</strong>
            {viewMode === "Giorgia" ? " (assistente Stefano)" : ""}
            {(viewMode === "Stefano" || viewMode === "Giorgia") ? " — commerciali" : " — residenziali"}
            {" · "}{filteredProperties.length} annunci
          </span>
          <button
            onClick={() => onViewModeChange("titolare")}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            Torna a vista titolare
          </button>
        </div>
      )}
      {isTitolare && !isAgentView && (
        <div className="bg-amber-50/50 border border-amber-200 rounded-sm px-4 py-2">
          <span className="text-sm text-zinc-600">
            Vista titolare — panoramica di tutti gli annunci. Clicca su un agente per vedere la sua schermata.
          </span>
        </div>
      )}

      {/* Today assignments — solo Titolare */}
      {isTitolare && todayAssignments && (
        <div data-testid="today-assignments" className="border border-zinc-200 bg-white rounded-sm px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider shrink-0">
            Oggi {todayAssignments.date}
          </span>
          <span className="text-xs text-zinc-500 shrink-0">
            {todayAssignments.total === 0
              ? "Nessun immobile assegnato"
              : `${todayAssignments.total} immobil${todayAssignments.total === 1 ? "e assegnato" : "i assegnati"}`}
          </span>
          <div className="flex items-center gap-3 flex-wrap">
            {todayAssignments.agents.map(a => (
              <span key={a.agent} className={`flex items-center gap-1 text-xs font-semibold ${a.count > 0 ? "text-zinc-800" : "text-zinc-400"}`}>
                {a.agent}
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${a.count > 0 ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-400"}`}>
                  {a.count}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}


      {/* Segnalazioni — solo Titolare */}
      {isTitolare && segnalazioni.length > 0 && (
        <div data-testid="segnalazioni-section" className="border-2 border-orange-300 bg-orange-50/50 rounded-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-orange-600" />
            <h3 className="text-sm font-bold text-zinc-900">Segnalazioni Agenti</h3>
            <span className="text-[10px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded-full">{segnalazioni.length}</span>
          </div>
          {segnalazioni.map(s => (
            <SegnalazioneCard key={s.id} segnalazione={s} onResolve={(id) => setSegnalazioni(prev => prev.filter(x => x.id !== id))} />
          ))}
        </div>
      )}

      {/* Stats */}
      <StatsCards stats={filteredStats} loading={loading} activeFilter={statusFilter} onFilterClick={setStatusFilter} />

      {/* Filters */}
      <FilterBar
        filters={filters}
        portals={portals}
        onApplyFilters={onApplyFilters}
      />

      {/* Non-Esclusivi (multi-agenzia) — solo Titolare */}
      {isTitolare && multiAgency.length > 0 && (
        <div data-testid="multi-agency-section" className="border-2 border-blue-200 bg-blue-50/40 rounded-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wide">Opportunità Non-Esclusive</h3>
            <span className="text-[10px] font-bold bg-blue-500 text-white px-1.5 py-0.5 rounded-full">{multiAgency.length}</span>
            <span className="text-xs text-blue-600 ml-1">Stessa proprietà su più portali di agenzie</span>
          </div>
          <div className="space-y-2">
            {multiAgency.map((p, i) => (
              <div key={i} className="bg-white border border-blue-100 rounded-sm px-3 py-2 flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm shrink-0">{p.ref_code}</span>
                <span className="text-sm font-semibold text-zinc-800 flex-1 min-w-0 truncate">{p.title}</span>
                <span className="text-xs text-zinc-500 shrink-0">{p.zone}</span>
                <span className="text-sm font-bold text-zinc-900 shrink-0">€ {p.price?.toLocaleString("it-IT")}</span>
                <div className="flex items-center gap-1 flex-wrap">
                  {(p.agency_portals || []).map((portal, j) => (
                    <span key={j} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-sm font-semibold">{portal}</span>
                  ))}
                </div>
                {p.source_urls && (
                  <a href={p.source_urls[0]} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-blue-600 underline shrink-0">Vedi annuncio</a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Properties List */}
      <div data-testid="properties-list" className="space-y-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-zinc-900 tracking-tight">
            {isAgentView
              ? teamView
                ? "Vista Team — tutti gli annunci"
                : "I Miei Annunci"
              : "Annunci trovati"}
          </h2>
          <span className="text-xs tracking-wider uppercase font-semibold text-zinc-400">
            {sortedProperties.length} risultati
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : sortedProperties.length === 0 ? (
          <div className="text-center py-20 text-zinc-400 text-sm">
            {isAgentView
              ? teamView
                ? "Nessun annuncio trovato per il team."
                : `Nessun annuncio assegnato a ${viewMode}.`
              : "Nessun annuncio trovato con i filtri selezionati."}
          </div>
        ) : (
          (() => {
            let lastAgent = null;
            return sortedProperties.map((prop, idx) => {
              const agentLabel = teamView && sortByAgent ? prop.assigned_agent || "Non assegnato" : null;
              const showHeader = agentLabel && agentLabel !== lastAgent;
              if (showHeader) lastAgent = agentLabel;
              const agentCount = showHeader
                ? sortedProperties.filter(p => (p.assigned_agent || "Non assegnato") === agentLabel).length
                : 0;
              return (
                <div key={prop.id}>
                  {showHeader && (
                    <div className="flex items-center gap-3 mt-4 mb-2 px-1">
                      <div className="h-px flex-1 bg-zinc-200" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">
                        {agentLabel} — {agentCount} immobili
                      </span>
                      <div className="h-px flex-1 bg-zinc-200" />
                    </div>
                  )}
                  <div className="animate-fade-in-up" style={{ animationDelay: `${Math.min(idx * 20, 300)}ms` }}>
                    <PropertyCard
                      property={prop}
                      allPortals={portals}
                      allComuni={comuni}
                      onUpdateStatus={onUpdateStatus}
                      readOnly={!isTitolare && teamView && (prop.assigned_agent || "") !== currentAgentFirstName}
                      canChangeCategory={isTitolare}
                      canReport={!isTitolare && !teamView}
                      currentUser={user}
                    />
                  </div>
                </div>
              );
            });
          })()
        )}
      </div>
    </div>
  );
}
